import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { ProgressBar, Badge } from 'react-bootstrap'
import { useQueryClient } from 'react-query'
import { baseUrl } from 'api/helpers'
import AuthContext from 'context/auth-context'
import { useContext } from 'react'
import axios from 'axios'

const ReceiptDropzone = ({ onUploadSuccess, onUploadError, onAllUploadsComplete, scanType = 'receipt' }) => {
  const [uploadQueue, setUploadQueue] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)

  const uploadSingleFile = async (file, index) => {
    const url = `${baseUrl}/receipt_scans`
    const formData = new FormData()
    formData.append('receipt_scan[receipt_image]', file)
    formData.append('receipt_scan[scan_type]', scanType)

    try {
      setUploadQueue((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, status: 'uploading' } : item
        )
      )

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      })

      setUploadQueue((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, status: 'processing', receiptScan: response.data.receipt_scan }
            : item
        )
      )

      if (onUploadSuccess) {
        onUploadSuccess(response.data.receipt_scan)
      }

      return { success: true, data: response.data }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Failed to upload'

      setUploadQueue((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, status: 'error', error: errorMessage } : item
        )
      )

      if (onUploadError) {
        onUploadError(error)
      }

      return { success: false, error }
    }
  }

  const processUploadQueue = async (files) => {
    setIsUploading(true)

    // Upload files sequentially
    for (let i = 0; i < files.length; i++) {
      await uploadSingleFile(files[i].file, i)
    }

    setIsUploading(false)
    queryClient.invalidateQueries(['receipt-scans'])

    // Check results
    setUploadQueue((prev) => {
      const successful = prev.filter((item) => item.status === 'processing').length
      const failed = prev.filter((item) => item.status === 'error').length

      if (successful > 0 && failed === 0) {
        toast.success(`${successful} ${scanType === 'invoice' ? 'invoice' : 'receipt'}${successful > 1 ? 's' : ''} uploaded successfully`)
      } else if (successful > 0 && failed > 0) {
        toast.warning(`${successful} uploaded, ${failed} failed`)
      } else if (failed > 0) {
        toast.error(`Failed to upload ${failed} ${scanType === 'invoice' ? 'invoice' : 'receipt'}${failed > 1 ? 's' : ''}`)
      }

      return prev
    })

    if (onAllUploadsComplete) {
      onAllUploadsComplete()
    }
  }

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach((rejection) => {
          if (rejection.errors.some((e) => e.code === 'file-too-large')) {
            toast.error(`${rejection.file.name} is too large. Maximum size is 10MB.`)
          } else if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
            toast.error(`${rejection.file.name} has invalid file type.`)
          }
        })
      }

      if (acceptedFiles.length > 250) {
        toast.error('You can only upload 250 files at a time.')
        return
      }

      if (acceptedFiles.length > 0) {
        const newQueue = acceptedFiles.map((file) => ({
          file,
          name: file.name,
          status: 'pending', // pending, uploading, processing, error
          error: null,
          receiptScan: null
        }))

        setUploadQueue(newQueue)
        processUploadQueue(newQueue)
      }
    },
    [token]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.tiff'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10485760, // 10MB
    multiple: true,
    disabled: isUploading
  })

  const clearQueue = () => {
    setUploadQueue([])
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>
      case 'uploading':
        return <Badge bg="primary">Uploading</Badge>
      case 'processing':
        return <small className="text-success">Uploaded</small>
      case 'error':
        return <Badge bg="danger">Failed</Badge>
      default:
        return null
    }
  }

  const completedCount = uploadQueue.filter(
    (item) => item.status === 'processing' || item.status === 'error'
  ).length
  const totalCount = uploadQueue.length
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  return (
    <div>
      <div
        {...getRootProps()}
        className={`receipt-dropzone ${isDragActive ? 'active' : ''} ${isUploading ? 'uploading' : ''}`}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.3s ease',
          opacity: isUploading ? 0.6 : 1
        }}
      >
        {/* react-dropzone keeps a real (hidden) file input, which is what
            cy.selectFile drives — do not try to simulate drag events. */}
        <input data-testid="receipt-dropzone-input" {...getInputProps()} />
        {isDragActive ? (
          <p className="mb-0 small">Drop the {scanType === 'invoice' ? 'invoices' : 'receipts'} here...</p>
        ) : (
          <div className="d-flex align-items-center justify-content-center gap-3">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: '#6c757d', flexShrink: 0 }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <div className="text-start">
              <span className="small">
                <strong>Drag & drop {scanType === 'invoice' ? 'invoices' : 'receipts'} here, or click to select</strong>
              </span>
              <span className="text-muted small ms-2">
                PNG, JPG, TIFF, PDF up to 10MB and 10 pages
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Progress Section */}
      {uploadQueue.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              Upload Progress ({completedCount} of {totalCount})
            </h6>
            {!isUploading && (
              <button
                className="btn btn-sm btn-light"
                onClick={clearQueue}
              >
                Close
              </button>
            )}
          </div>

          <ProgressBar
            now={progressPercent}
            label={`${progressPercent}%`}
            animated={isUploading}
            striped={isUploading}
            className="mb-3"
          />

          <div
            className="upload-queue-list"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            {uploadQueue.map((item, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div className="text-truncate" style={{ maxWidth: '70%' }}>
                  <small>{item.name}</small>
                  {item.error && (
                    <div className="text-danger small">{item.error}</div>
                  )}
                </div>
                <div>{getStatusBadge(item.status)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

ReceiptDropzone.propTypes = {
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  onAllUploadsComplete: PropTypes.func,
  scanType: PropTypes.string
}

export default ReceiptDropzone
